import { resourcesSubscriber } from '@/mqtt/resources'
import SlideApi from '@/api/slide'
import util from '@/util'
import * as Sentry from '@sentry/vue'
import AudienceApi from '@/api/audience'

const setPresentationAction = (context, payload) => {
  context.commit('setPresentation', payload)
}

const patchPresentationAction = (context, payload) => {
  context.commit('patchPresentation', payload)
}

const setActiveTabAction = (store, payload) => {
  store.commit('setActiveTab', payload.activeTab || payload)
}

let isFirstLoad = true

const subscribeSlideChangeAction = ({
  dispatch, commit, getters, state,
}, payload) => {
  // If presenter is calling the old endpoint /update-next-slide (i.e. turning off Unleash flag), then do not subscribe the MQTT topic.
  // Subscribing to it may lead to difference in slideId between the first load and the retained message from EMQX.
  if (!getters['featureToggles/canSubscribeMqttChangeSlideEvent']) {
    return
  }
  if (!payload.id) return
  resourcesSubscriber.subscribeResourceUri(`/LIVE_SESSION/${payload.id}/ACTIVE_ACTIVITY`, async (message, packet) => {
    const msg = JSON.parse(message)
    const slideId = msg.activeActivityId
    if (!packet.retain) {
      commit('updateSlideV2Timestamp', {
        id: slideId,
        timestamp: new Date().getTime(),
      })
    }
    // Check if the slideId in the last retained message from EMQX is somehow different from the slide that got the first load (via endpoint /audience-data/:uniqueAccessCode endpoint)
    // These code are short-lived, will be removed soon, so no need to care about coding style
    if (packet.retain &&
        state.presentation &&
        state.presentation.activeSlide &&
        slideId !== state.presentation.activeSlide && isFirstLoad
    ) {
      Sentry.captureException(new Error('SlideId from EMQX retained msg is different from the slideId from first load'))
    }
    isFirstLoad = false

    if (!slideId) return
    if (getters['featureToggles/canUseNewChangeSlideMechanism']) {
      commit('updateSlideV2', {
        id: slideId,
        slideTimestamp: msg.slideTimestamp,
      })
    }

    const slide = await SlideApi.getSlideDetailV2(slideId)
    slide.slideTimestamp = msg.slideTimestamp

    if (!slide.id) return

    // TODO: find a way to avoid setting an empty slide before setting the actual slide. This is a tech debt that existed long before.
    dispatch('setSlideV2Action', { id: null })
    setImmediate(() => {
      dispatch('setSlideV2Action', util.formatSlide(slide))
    }, 0)
  }, { once: true })
}

const unsubscribeSlideChangeAction = (_, payload) => {
  resourcesSubscriber.unsubscribeResourceUri(`/LIVE_SESSION/${payload.id}/ACTIVE_ACTIVITY`)
}

const dispatchUpdateFeatureContextWithAwait = async (presentation, dispatch) => {
  // Tạo một Promise để đợi onReady hoàn thành
  // eslint-disable-next-line promise/avoid-new
  return new Promise((resolve) => {
    dispatch('featureToggles/updateFeatureContext', {
      userId: presentation.userId,
      properties: {
        uniqueAccessCode: presentation.uniqueAccessCode,
        email: presentation && presentation.user && presentation.user.email,
      },
      presentationCreatorEmail: presentation.creator.email,
      onReady: async () => {
        try {
          // Chạy hàm onReady khi nó được gọi
          resolve() // Resolve khi onReady hoàn tất
        } catch (error) {
          resolve() // Reject nếu có lỗi trong onReady
        }
      },
    })
  })
}

const getPresentationByCode = async ({ dispatch }, { uniqueAccessCode, params }) => {
  // eslint-disable-next-line no-useless-catch
  try {
    const response = await AudienceApi.getAudienceDataCDN(uniqueAccessCode, params)
    const presentation = response.body.presentation || response.body
    // Need to update presentationCreatorEmail for the feature flag in order to correctly retrieve features based on the creator, so that both the presenter and the audience share the same flag and avoid errors.
    try {
      await dispatchUpdateFeatureContextWithAwait(presentation, dispatch)
    } catch (error) {
      Sentry.captureException(new Error('Update Context Creator Unleash Error'), {
        extra: {
          error,
          presentation,
        },
      })
    }
    return response
  // eslint-disable-next-line sonarjs/no-useless-catch
  } catch (error) {
    throw error
  }
}

export default {
  setPresentationAction,
  setPresentation: setPresentationAction,
  patchPresentationAction,
  setActiveTabAction,
  subscribeSlideChangeAction,
  unsubscribeSlideChangeAction,
  getPresentationByCode,
}
